import React, {
  createContext,
  useContext,
  useCallback,
  useEffect,
  useMemo,
  useState
} from "react";
import {
  ApiAppointment,
  cancelAppointment as cancelAppointmentApi,
  createAppointment,
  listAppointments
} from "../services/videoAppointments";
import moment from "moment";
import { useUserState } from "./user";
import useFichaClinica from "../hooks/useFichaClinica";

export const DEFAULT_DURATION_MINUTES = 25;

/** Shortest consultation the backend accepts — scheduling is blocked below it. */
export const MIN_DURATION_MINUTES = 5;

// How wide a window we fetch around "now" so the calendar, the upcoming list and
// the full history are all populated from a single request.
const WINDOW_BACK_DAYS = 365;
const WINDOW_FORWARD_DAYS = 365;

/** UI-facing appointment shape used throughout the app. */
export type Appointment = {
  id: string;
  externalId?: string;
  title: string;
  /** Identity of the appointment — the clinical file it belongs to. */
  clinicalFileId: string | null;
  /** Resolved from the doctor's clinical-file list via clinicalFileId. */
  patientName: string;
  notes?: string;
  durationMinutes: number;
  /** Raw backend status code (meaning confirmed via the API team). */
  state?: number | null;
  /** True when cancelled in this session (kept locally to show in history). */
  cancelled?: boolean;
  start: Date;
  end: Date;
};

export type AddAppointmentInput = {
  start: Date;
  clinicalFileId: string;
  /** Optional — only used for optimistic display until clinical files resolve the name. */
  patientName?: string;
  durationMinutes?: number;
  notes?: string;
};

type AppointmentsContextValue = {
  appointments: Appointment[];
  loading: boolean;
  error: unknown;
  refresh: () => Promise<void>;
  addAppointment: (
    input: AddAppointmentInput
  ) => Promise<Appointment | undefined>;
  cancelAppointment: (id: string) => Promise<void>;
};

const AppointmentsContext = createContext<AppointmentsContextValue | null>(
  null
);

// The API rejects an empty `observations`, but in our UI notes are optional.
// So when there are no notes we send this sentinel, and strip it back to "" on
// read — keeping the notes field truly optional without breaking the API.
const NO_NOTES_PLACEHOLDER = "Videoconsulta";

// Backend VideoCallAppointmentState code for a cancelled appointment.
const CANCELLED_STATE = 4;

// `observations` is plain clinical notes. Older rows (created during the early
// localStorage→API migration) may carry a {patientName,notes} JSON blob — read
// those gracefully so nothing is lost, but the name now comes from the clinical file.
function readObservations(
  raw: string | null | undefined
): { legacyName: string; notes: string } {
  if (!raw) return { legacyName: "", notes: "" };
  try {
    const parsed = JSON.parse(raw);
    if (
      parsed &&
      typeof parsed === "object" &&
      ("patientName" in parsed || "notes" in parsed)
    ) {
      return {
        legacyName: parsed.patientName ?? "",
        notes: parsed.notes ?? ""
      };
    }
  } catch {
    // not JSON — it's a plain notes string
  }
  // Strip the "no notes" sentinel back to empty.
  return { legacyName: "", notes: raw === NO_NOTES_PLACEHOLDER ? "" : raw };
}

function mapApiToUi(
  api: ApiAppointment,
  patientByFileId: Map<string, any>
): Appointment {
  const fileId = api.clinicalFileId;
  const clinicalFile = fileId ? patientByFileId.get(fileId) : undefined;
  const { legacyName, notes } = readObservations(api.observations);
  const start = new Date(api.scheduledDate);
  // expectedDuration may be "30" or "30m" — parseInt handles both.
  const durationMinutes =
    parseInt(api.expectedDuration, 10) || DEFAULT_DURATION_MINUTES;
  const patientName = clinicalFile?.name || legacyName || "";
  return {
    id: api.id,
    externalId: api.externalId,
    // Used only as react-big-calendar's title accessor (tooltip/agenda); the
    // visible event label is rendered by a custom component. Kept language-neutral.
    title: patientName,
    clinicalFileId: fileId,
    patientName,
    notes,
    durationMinutes,
    state: api.state ?? null,
    start,
    end: new Date(start.getTime() + durationMinutes * 60 * 1000)
  };
}

const byStartAsc = (a: Appointment, b: Appointment) =>
  a.start.getTime() - b.start.getTime();

export default function AppointmentsProvider(
  props: React.PropsWithChildren<{}>
) {
  const { token } = useUserState();
  const { data: clinicalFiles } = useFichaClinica();
  const [rawAppointments, setRawAppointments] = useState<ApiAppointment[]>([]);
  // Appointments cancelled in this session. Kept (not removed) so they show up
  // in history as "cancelled" immediately — the GET excludes cancelled ones, so
  // we hold onto the record locally to surface it without a round-trip.
  const [cancelledById, setCancelledById] = useState<
    Map<string, ApiAppointment>
  >(new Map());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<unknown>(null);

  // clinicalFileId → patient ("contact") record, so we can resolve the name.
  const patientByFileId = useMemo(() => {
    const map = new Map<string, any>();
    // The list endpoint may return an array or a { items: [...] } wrapper.
    const list: any[] = Array.isArray(clinicalFiles)
      ? (clinicalFiles as any[])
      : (clinicalFiles as any)?.items ?? [];
    list.forEach(clinicalFile => {
      const id =
        clinicalFile?.clinicalFileId ??
        clinicalFile?._id ??
        clinicalFile?.fileId ??
        clinicalFile?.scriboPartition;
      if (id) map.set(id, clinicalFile);
    });
    return map;
  }, [clinicalFiles]);

  const appointments = useMemo(() => {
    // Merge server appointments with session-cancelled ones (cancelled wins).
    const byId = new Map<string, ApiAppointment>();
    rawAppointments.forEach(appointment =>
      byId.set(appointment.id, appointment)
    );
    cancelledById.forEach((appointment, id) => byId.set(id, appointment));
    return (
      Array.from(byId.values())
        // Drop truly-deleted rows, but keep cancellations (CANCELLED state) even
        // when they come back flagged as deleted — they belong in history.
        .filter(
          appointment =>
            !appointment.deleted || appointment.state === CANCELLED_STATE
        )
        .map(appointment => {
          const ui = mapApiToUi(appointment, patientByFileId);
          return cancelledById.has(appointment.id)
            ? { ...ui, cancelled: true }
            : ui;
        })
        .sort(byStartAsc)
    );
  }, [rawAppointments, cancelledById, patientByFileId]);

  const refresh = useCallback(async () => {
    if (!token) return;
    try {
      setLoading(true);
      const now = Date.now();
      const from = new Date(
        now - WINDOW_BACK_DAYS * 24 * 60 * 60 * 1000
      ).toISOString();
      const to = new Date(
        now + WINDOW_FORWARD_DAYS * 24 * 60 * 60 * 1000
      ).toISOString();
      const list = await listAppointments(token, from, to);
      setRawAppointments(list);
      setError(null);
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  }, [token]);

  // Initial load + reload whenever the token becomes available.
  useEffect(() => {
    refresh();
  }, [refresh]);

  // Sync strategy: instead of polling, refetch whenever the doctor returns to
  // the tab/window. Covers switching apps, refreshing, or coming back from the
  // mobile app — without hammering the API while idle.
  useEffect(() => {
    const onFocus = () => refresh();
    const onVisibility = () => {
      if (document.visibilityState === "visible") refresh();
    };
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [refresh]);

  const addAppointment = useCallback(
    async (input: AddAppointmentInput) => {
      if (!token) return undefined;
      const created = await createAppointment(token, {
        // Same ISO format the notify endpoints use — moment().format() keeps the
        // local offset (e.g. +01:00). Date.toISOString() would send UTC "Z",
        // which the API does not handle the same way.
        scheduledDate: moment(input.start).format(),
        // The deployed API stores durations as "<minutes>m" (e.g. "30m");
        // sending a bare number 500s the server.
        expectedDuration: `${input.durationMinutes ??
          DEFAULT_DURATION_MINUTES}m`,
        // `observations` is required (non-empty) by the API.
        observations: input.notes?.trim() || NO_NOTES_PLACEHOLDER,
        clinicalFileId: input.clinicalFileId
      });
      setRawAppointments(prev => [...prev, created]);
      return mapApiToUi(created, patientByFileId);
    },
    [token, patientByFileId]
  );

  const cancelAppointment = useCallback(
    async (id: string) => {
      if (!token) return;
      const appointment =
        rawAppointments.find(candidate => candidate.id === id) ??
        cancelledById.get(id);
      // Optimistically mark as cancelled (keep it, so it moves to history).
      if (appointment) {
        setCancelledById(prev => new Map(prev).set(id, appointment));
      }
      try {
        await cancelAppointmentApi(token, id);
      } catch (err) {
        setError(err);
        setCancelledById(prev => {
          const next = new Map(prev);
          next.delete(id);
          return next;
        });
        throw err;
      }
    },
    [token, rawAppointments, cancelledById]
  );

  const value: AppointmentsContextValue = {
    appointments,
    loading,
    error,
    refresh,
    addAppointment,
    cancelAppointment
  };

  return (
    <AppointmentsContext.Provider value={value}>
      {props.children}
    </AppointmentsContext.Provider>
  );
}

export function useAppointmentsContext() {
  const context = useContext(AppointmentsContext);
  if (!context) {
    throw new Error(
      "useAppointmentsContext must be used within an AppointmentsProvider"
    );
  }
  return context;
}
